import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState } from 'react';
import { Button, Input, Text, useLocalizeMessage, useToastNotification } from 'libs.nucleus';
import { isChoiceElementType } from '../constants/consent_element_types.constants';
import { useConsentActionsContext } from '../hooks/use_consent_context.hook';
const elementLabel = (element) => element.config.ec__label?.trim() || element.config.ec__title?.trim() || element.type.replaceAll('_', ' ');
const unplacedItems = (document) => {
    const items = [];
    for (const element of document.elements) {
        if (element.placed === false) {
            items.push({
                id: element.id,
                label: elementLabel(element),
                page: element.page,
                apply: (page) => ({ ...element, page, placed: true }),
            });
        }
        if (isChoiceElementType(element.type)) {
            for (const option of element.config.ec__options ?? []) {
                if (option.placed === false) {
                    items.push({
                        id: `${element.id}:${option.id}`,
                        label: `${elementLabel(element)} — ${option.value || option.key}`,
                        page: option.page,
                        apply: (page) => ({
                            ...element,
                            config: {
                                ...element.config,
                                ec__options: (element.config.ec__options ?? []).map((entry) => entry.id === option.id ? { ...entry, page, placed: true } : entry),
                            },
                        }),
                    });
                }
            }
        }
    }
    return items;
};
export const CarryForwardReview = ({ document }) => {
    const translate = useLocalizeMessage();
    const { addNotification } = useToastNotification();
    const { updateElement, updateDocument } = useConsentActionsContext();
    const { carryForward } = document;
    const items = unplacedItems(document);
    const showBanner = Boolean(carryForward && !carryForward.failed && !carryForward.placementsConfirmed);
    const [draftPages, setDraftPages] = useState({});
    if (!showBanner && items.length === 0) {
        return null;
    }
    const handleConfirm = async () => {
        if (!carryForward) {
            return;
        }
        try {
            await updateDocument({
                ...document,
                carryForward: { ...carryForward, placementsConfirmed: true },
            });
        }
        catch {
            addNotification({
                type: 'error',
                title: translate('Could not confirm placements'),
                subtitle: translate('The draft was not updated. Try Confirm placements again.'),
            });
        }
    };
    const handleApply = (item) => {
        if (document.totalPages < 1) {
            return;
        }
        const requested = Number.parseInt(draftPages[item.id] ?? String(item.page), 10);
        if (Number.isNaN(requested)) {
            return;
        }
        const page = Math.min(document.totalPages, Math.max(1, requested));
        updateElement(document.id, item.apply(page));
    };
    return (_jsxs("div", { className: 'nucleus-flex nucleus-flex-col nucleus-gap-3 nucleus-px-4 nucleus-py-3', "data-testid": 'carry-forward-review', children: [showBanner ? (_jsxs("div", { className: 'nucleus-flex nucleus-items-center nucleus-justify-between nucleus-gap-4 nucleus-rounded-lg nucleus-border nucleus-border-yellow-300 nucleus-bg-yellow-50 nucleus-p-3', "data-testid": 'carry-forward-banner', children: [_jsx(Text, { as: 'p', size: 'sm', children: translate(carryForward?.fromName
                            ? 'Placements were copied from {name} (version {version}), not placed on this PDF. Confirm the positions to enable Publish.'
                            : 'Placements were copied from version {version}, not placed on this PDF. Confirm the positions to enable Publish.', {
                            version: String(carryForward?.fromVersion ?? ''),
                            name: carryForward?.fromName ?? '',
                        }) }), _jsx(Button, { size: 'sm', label: translate('Confirm placements'), onClick: handleConfirm, dataTestId: 'carry-forward-confirm' })] })) : null, items.length > 0 ? (_jsxs("div", { "data-testid": 'carry-forward-unplaced-rail', children: [_jsx(Text, { as: 'p', size: 'sm', className: 'nucleus-mb-2 nucleus-font-medium', children: translate('Unplaced elements') }), _jsx("ul", { className: 'nucleus-flex nucleus-flex-col nucleus-gap-2', children: items.map((item) => (_jsxs("li", { className: 'nucleus-flex nucleus-items-end nucleus-gap-2', "data-testid": `unplaced-item-${item.id}`, children: [_jsx(Text, { as: 'span', size: 'sm', className: 'nucleus-flex-1', children: item.label }), _jsx(Input, { id: `unplaced-page-${item.id}`, label: translate('Page'), type: 'number', min: 1, max: document.totalPages, width: 'xs', value: draftPages[item.id] ?? String(item.page), onChange: (event) => setDraftPages((current) => ({ ...current, [item.id]: event.target.value })), dataTestId: `unplaced-page-input-${item.id}` }), _jsx(Button, { size: 'sm', variant: 'secondary', label: translate('Apply'), onClick: () => handleApply(item), disabled: document.totalPages < 1, dataTestId: `unplaced-apply-${item.id}` })] }, item.id))) })] })) : null] }));
};
